(() => {
  const STORAGE_KEYS = {
    wishlist: 'theme:wishlist-products',
    savedForLater: 'theme:save-for-later'
  };

  const pageType = window.themeVariables?.settings?.pageType || '';
  const shopifyRoot = window.Shopify?.routes?.root || '/';

  const parseJson = (value, fallback = []) => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  };

  const toInt = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const readStorageList = (key) => {
    try {
      return parseJson(localStorage.getItem(key), []);
    } catch {
      return [];
    }
  };

  const writeStorageList = (key, items) => {
    try {
      localStorage.setItem(key, JSON.stringify(items));
      return true;
    } catch {
      return false;
    }
  };

  const dispatchDocumentEvent = (name, detail) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const collectBundledSections = () => {
    const sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections }
      })
    );

    return sections;
  };

  const dispatchCartChange = (baseEvent, cart) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent,
          cart
        }
      })
    );
  };

  const getWishlistItems = () => {
    return readStorageList(STORAGE_KEYS.wishlist).filter((item) => toInt(item?.productId));
  };

  const setWishlistItems = (items) => {
    if (!writeStorageList(STORAGE_KEYS.wishlist, items)) {
      return false;
    }

    dispatchDocumentEvent('wishlist:change', { items });
    syncWishlistUi();
    return true;
  };

  const syncWishlistButtons = (root = document) => {
    const wishlistIds = new Set(getWishlistItems().map((item) => toInt(item.productId)));

    root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const productId = toInt(button.dataset.productId);
      if (!productId) {
        return;
      }

      const isActive = wishlistIds.has(productId);
      const addLabel = button.dataset.addLabel || 'Add to wishlist';
      const removeLabel = button.dataset.removeLabel || 'Remove from wishlist';

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      const labelElement = button.querySelector('[data-wishlist-label]');
      if (labelElement) {
        if (labelElement.textContent !== (isActive ? removeLabel : addLabel)) labelElement.textContent = isActive ? removeLabel : addLabel;
      }
    });
  };

  const syncWishlistCount = () => {
    const count = getWishlistItems().length;

    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      if (element.textContent !== String(count)) element.textContent = String(count);
      element.classList.toggle('is-visible', count > 0);
      element.hidden = count === 0;
    });
  };

  const syncWishlistUi = () => {
    syncWishlistButtons(document);
    syncWishlistCount();
  };

  const buildWishlistItem = (dataset) => {
    return {
      productId: toInt(dataset.productId),
      variantId: toInt(dataset.variantId),
      title: (dataset.productTitle || '').trim(),
      url: dataset.productUrl || '',
      image: dataset.productImage || '',
      price: dataset.productPrice || '',
      addedAt: Date.now()
    };
  };

  const getSavedForLaterItems = () => {
    return readStorageList(STORAGE_KEYS.savedForLater).filter((item) => toInt(item?.variantId));
  };

  const setSavedForLaterItems = (items) => {
    if (!writeStorageList(STORAGE_KEYS.savedForLater, items)) {
      return false;
    }

    dispatchDocumentEvent('save-for-later:change', { items });
    return true;
  };

  const addOrUpdateSavedForLater = (item) => {
    const items = getSavedForLaterItems();
    const existingIndex = items.findIndex((savedItem) => toInt(savedItem.variantId) === item.variantId);

    if (existingIndex >= 0) {
      items[existingIndex] = {
        ...items[existingIndex],
        ...item,
        quantity: (toInt(items[existingIndex].quantity) || 0) + (toInt(item.quantity) || 1),
        addedAt: Date.now()
      };
    } else {
      items.unshift(item);
    }

    return items;
  };

  const removeSavedForLaterByVariant = (variantId) => {
    return getSavedForLaterItems().filter((item) => toInt(item.variantId) !== variantId);
  };

  const addVariantToCart = async (variantId, quantity = 1, options = {}) => {
    const validVariantId = toInt(variantId);
    const validQuantity = Math.max(1, toInt(quantity) || 1);

    if (!validVariantId) {
      throw new Error('Missing variant identifier.');
    }

    const sections = collectBundledSections();
    const formData = new FormData();

    formData.set('id', String(validVariantId));
    formData.set('quantity', String(validQuantity));

    if (sections.length > 0) {
      formData.set('sections', sections.join(','));
    }

    formData.set('sections_url', `${window.location.pathname}${window.location.search}`);

    const addResponse = await fetch(`${shopifyRoot}cart/add.js`, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    let addResponseJson = {};

    try {
      addResponseJson = await addResponse.json();
    } catch {
      addResponseJson = {};
    }

    if (!addResponse.ok) {
      throw new Error(addResponseJson.description || addResponseJson.message || 'Unable to add this item to cart.');
    }

    const cartResponse = await fetch(`${shopifyRoot}cart.js`);
    const cart = await cartResponse.json();

    if (addResponseJson.sections) {
      cart.sections = addResponseJson.sections;
    }

    dispatchCartChange(options.baseEvent || 'custom:add', cart);

    if (pageType === 'cart' && options.reloadCartPage !== false) {
      window.location.reload();
    }

    return cart;
  };

  const removeCartLine = async (lineKey) => {
    const sections = collectBundledSections();

    const response = await fetch(`${shopifyRoot}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sections.join(',')
      })
    });

    if (!response.ok) {
      const errorContent = await response.json().catch(() => ({}));
      throw new Error(errorContent.description || errorContent.message || 'Unable to update your cart.');
    }

    const cart = await response.json();

    if (pageType === 'cart') {
      window.location.reload();
      return cart;
    }

    dispatchCartChange('save-for-later', cart);
    return cart;
  };

  class WishlistItems extends HTMLElement {
    connectedCallback() {
      this._onWishlistChanged = this.render.bind(this);
      document.addEventListener('wishlist:change', this._onWishlistChanged);
      this.addEventListener('click', this._onClick.bind(this));
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this._onWishlistChanged);
    }

    async _onClick(event) {
      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        const productId = toInt(removeButton.dataset.productId);
        if (!productId) {
          return;
        }

        const items = getWishlistItems().filter((item) => toInt(item.productId) !== productId);
        setWishlistItems(items);
        return;
      }

      const addButton = event.target.closest('[data-wishlist-add]');
      if (!addButton) {
        return;
      }

      event.preventDefault();

      if (addButton.getAttribute('aria-busy') === 'true') {
        return;
      }

      const variantId = toInt(addButton.dataset.variantId);
      if (!variantId) {
        return;
      }

      addButton.setAttribute('aria-busy', 'true');

      try {
        await addVariantToCart(variantId, 1, { baseEvent: 'wishlist:add' });
      } catch (error) {
        window.alert(error.message);
      } finally {
        addButton.removeAttribute('aria-busy');
      }
    }

    render() {
      const items = getWishlistItems();
      const emptyMessage = this.dataset.emptyMessage || 'Your wishlist is empty.';
      const removeLabel = this.dataset.removeLabel || 'Remove';
      const addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';

      if (items.length === 0) {
        this.innerHTML = `<p class="text-subdued">${escapeHtml(emptyMessage)}</p>`;
        return;
      }

      this.innerHTML = `
        <div class="wishlist-items v-stack gap-5">
          ${items
            .map((item) => {
              const productId = toInt(item.productId);
              const variantId = toInt(item.variantId);
              const imageMarkup = item.image
                ? `<a href="${escapeHtml(item.url || '#')}" class="wishlist-item__media"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy"></a>`
                : '';

              return `
                <article class="wishlist-item">
                  ${imageMarkup}
                  <div class="wishlist-item__content">
                    <a href="${escapeHtml(item.url || '#')}" class="link-faded-reverse">${escapeHtml(item.title || '')}</a>
                    ${item.price ? `<p class="text-subdued text-sm">${escapeHtml(item.price)}</p>` : ''}
                    <div class="h-stack gap-3 wrap">
                      <button type="button" class="button button--outline button--subdued button-sm" data-wishlist-add data-variant-id="${variantId || ''}" ${variantId ? '' : 'disabled'}>${escapeHtml(addToCartLabel)}</button>
                      <button type="button" class="link text-sm" data-wishlist-remove data-product-id="${productId || ''}">${escapeHtml(removeLabel)}</button>
                    </div>
                  </div>
                </article>
              `;
            })
            .join('')}
        </div>
      `;
    }
  }

  class SavedItemsList extends HTMLElement {
    connectedCallback() {
      this._onSavedChanged = this.render.bind(this);
      document.addEventListener('save-for-later:change', this._onSavedChanged);
      this.addEventListener('click', this._onClick.bind(this));
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('save-for-later:change', this._onSavedChanged);
    }

    async _onClick(event) {
      const removeButton = event.target.closest('[data-saved-remove]');
      if (removeButton) {
        event.preventDefault();
        const variantId = toInt(removeButton.dataset.variantId);
        if (!variantId) {
          return;
        }

        if (!setSavedForLaterItems(removeSavedForLaterByVariant(variantId))) {
          window.alert(this.dataset.errorMessage || 'Unable to update saved items.');
        }
        return;
      }

      const moveButton = event.target.closest('[data-saved-move]');
      if (!moveButton) {
        return;
      }

      event.preventDefault();

      if (moveButton.getAttribute('aria-busy') === 'true') {
        return;
      }

      const variantId = toInt(moveButton.dataset.variantId);
      const quantity = Math.max(1, toInt(moveButton.dataset.quantity) || 1);

      if (!variantId) {
        return;
      }

      moveButton.setAttribute('aria-busy', 'true');

      try {
        await addVariantToCart(variantId, quantity, { baseEvent: 'save-for-later:move', reloadCartPage: false });
        if (!setSavedForLaterItems(removeSavedForLaterByVariant(variantId))) {
          window.alert(this.dataset.errorMessage || 'Unable to update saved items.');
          return;
        }

        if (pageType === 'cart') {
          window.location.reload();
        }
      } catch (error) {
        window.alert(this.dataset.errorMessage || error.message);
      } finally {
        moveButton.removeAttribute('aria-busy');
      }
    }

    render() {
      const items = getSavedForLaterItems();
      const title = this.dataset.title || 'Saved for later';
      const emptyMessage = this.dataset.emptyMessage || 'No saved items yet.';
      const moveLabel = this.dataset.moveLabel || 'Move to cart';
      const removeLabel = this.dataset.removeLabel || 'Remove';

      if (items.length === 0) {
        if (this.dataset.showEmpty !== 'true') {
          this.hidden = true;
          this.innerHTML = '';
          return;
        }

        this.hidden = false;
        this.innerHTML = `
          <div class="saved-items-list__empty v-stack gap-2">
            <h3 class="h6">${escapeHtml(title)}</h3>
            <p class="text-subdued">${escapeHtml(emptyMessage)}</p>
          </div>
        `;
        return;
      }

      this.hidden = false;
      this.innerHTML = `
        <div class="saved-items-list v-stack gap-4">
          <h3 class="h6">${escapeHtml(title)}</h3>
          <div class="saved-items-list__items v-stack gap-4">
            ${items
              .map((item) => {
                const variantId = toInt(item.variantId);
                const quantity = Math.max(1, toInt(item.quantity) || 1);
                const imageMarkup = item.image
                  ? `<a href="${escapeHtml(item.url || '#')}" class="saved-item__media"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy"></a>`
                  : '';

                return `
                  <article class="saved-item">
                    ${imageMarkup}
                    <div class="saved-item__content">
                      <a href="${escapeHtml(item.url || '#')}" class="link-faded-reverse">${escapeHtml(item.title || '')}</a>
                      ${item.variantTitle ? `<p class="text-subdued text-sm">${escapeHtml(item.variantTitle)}</p>` : ''}
                      ${item.price ? `<p class="text-subdued text-sm">${escapeHtml(item.price)}</p>` : ''}
                      <div class="h-stack gap-3 wrap">
                        <button type="button" class="button button--outline button--subdued button-sm" data-saved-move data-variant-id="${variantId || ''}" data-quantity="${quantity}">${escapeHtml(moveLabel)}</button>
                        <button type="button" class="link text-sm" data-saved-remove data-variant-id="${variantId || ''}">${escapeHtml(removeLabel)}</button>
                      </div>
                    </div>
                  </article>
                `;
              })
              .join('')}
          </div>
        </div>
      `;
    }
  }

  class DiscountCodeForm extends HTMLElement {
    connectedCallback() {
      this._input = this.querySelector('[name="discount_code"]');
      this._button = this.querySelector('button');
      this._onClick = this._applyDiscount.bind(this);
      this._onKeyDown = this._onInputKeyDown.bind(this);
      this._button?.addEventListener('click', this._onClick);
      this._input?.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      this._button?.removeEventListener('click', this._onClick);
      this._input?.removeEventListener('keydown', this._onKeyDown);
    }

    _onInputKeyDown(event) {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      this._applyDiscount();
    }

    _applyDiscount() {
      const discountCode = this._input?.value?.trim();

      if (!discountCode) {
        this._input?.focus();
        return;
      }

      window.location.href = `${shopifyRoot}checkout?discount=${encodeURIComponent(discountCode)}`;
    }
  }

  const handleWishlistToggle = (button) => {
    const productId = toInt(button.dataset.productId);
    if (!productId) {
      return;
    }

    const nextItem = buildWishlistItem(button.dataset);
    if (!nextItem.productId) {
      return;
    }

    const currentItems = getWishlistItems();
    const existingIndex = currentItems.findIndex((item) => toInt(item.productId) === productId);

    if (existingIndex >= 0) {
      currentItems.splice(existingIndex, 1);
    } else {
      currentItems.unshift(nextItem);
    }

    if (!setWishlistItems(currentItems)) {
      window.alert('Unable to update your wishlist.');
    }
  };

  const handleSaveForLater = async (button) => {
    if (button.getAttribute('aria-busy') === 'true') {
      return;
    }

    const lineKey = button.dataset.lineKey;
    const variantId = toInt(button.dataset.variantId);

    if (!lineKey || !variantId) {
      return;
    }

    const currentItems = getSavedForLaterItems();
    const nextItems = addOrUpdateSavedForLater({
      variantId,
      quantity: Math.max(1, toInt(button.dataset.quantity) || 1),
      productId: toInt(button.dataset.productId),
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '',
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '',
      variantTitle: button.dataset.variantTitle || '',
      addedAt: Date.now()
    });

    if (!setSavedForLaterItems(nextItems)) {
      window.alert(button.dataset.errorMessage || 'Unable to save this item for later.');
      return;
    }

    button.setAttribute('aria-busy', 'true');

    try {
      await removeCartLine(lineKey);
    } catch (error) {
      setSavedForLaterItems(currentItems);
      window.alert(button.dataset.errorMessage || error.message);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  if (!window.customElements.get('wishlist-items')) {
    window.customElements.define('wishlist-items', WishlistItems);
  }

  if (!window.customElements.get('saved-items-list')) {
    window.customElements.define('saved-items-list', SavedItemsList);
  }

  if (!window.customElements.get('discount-code-form')) {
    window.customElements.define('discount-code-form', DiscountCodeForm);
  }

  document.addEventListener('click', (event) => {
    const wishlistToggle = event.target.closest('[data-wishlist-toggle]');
    if (wishlistToggle) {
      event.preventDefault();
      handleWishlistToggle(wishlistToggle);
      return;
    }

    const saveForLaterToggle = event.target.closest('[data-save-for-later]');
    if (saveForLaterToggle) {
      event.preventDefault();
      handleSaveForLater(saveForLaterToggle);
    }
  });

  document.addEventListener('cart-drawer:refreshed', syncWishlistUi);
  document.addEventListener('variant:change', syncWishlistUi);

  let pendingWishlistSync = false;
  const scheduleWishlistSync = () => {
    if (pendingWishlistSync) {
      return;
    }

    pendingWishlistSync = true;

    requestAnimationFrame(() => {
      pendingWishlistSync = false;
      syncWishlistUi();
    });
  };

  const observer = new MutationObserver(scheduleWishlistSync);

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEYS.wishlist) {
      syncWishlistUi();
      dispatchDocumentEvent('wishlist:change', { items: getWishlistItems() });
    }

    if (event.key === STORAGE_KEYS.savedForLater) {
      dispatchDocumentEvent('save-for-later:change', { items: getSavedForLaterItems() });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncWishlistUi, { once: true });
  } else {
    syncWishlistUi();
  }
})();
